import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
    notificationsApi,
    NotificationSummary,
} from "../../services/apis/notifications.api";
import { RootState } from "../store";
import {
    isNotificationInOrgScope,
    isNotificationInOrgStrict,
    parseNotificationDate,
} from "../../utils/notificationUtils";

export interface Notification {
    _id: string;
    title: string;
    message: string;
    severity: string;
    category: string;
    read: boolean;
    user: string;
    /** Organization this notification belongs to. Used to scope the sidebar
     * badges to the currently selected org. Optional/absent on legacy
     * notifications created before org-scoping (those simply don't count). */
    organization?: string | { _id: string };
    link?: string;
    priority: string;
    data?: any;
    expiresAt?: Date;
    createdAt?: string | Date;
}

export interface FetchUserNotificationsArgs {
    userId: string;
    from?: string;
    to?: string;
    append?: boolean;
}

export interface FetchNotificationSummaryArgs {
    userId: string;
    organization?: string;
}

export interface MarkNotificationsReadArgs {
    userId: string;
    organization?: string;
    category?: string;
}

interface NotificationsState {
    notifications: Notification[];
    summary: NotificationSummary;
    summaryOrganizationId?: string;
    hasMore: boolean;
    loading: boolean;
    summaryLoading: boolean;
    error: string | null;
}

const emptySummary: NotificationSummary = {
    totalUnread: 0,
    taskUnread: 0,
    auditUnread: 0,
};

const initialState: NotificationsState = {
    notifications: [],
    summary: emptySummary,
    hasMore: false,
    loading: false,
    summaryLoading: false,
    error: null,
};

const mergeNotifications = (
    existing: Notification[],
    incoming: Notification[]
): Notification[] => {
    const map = new Map<string, Notification>();
    for (const notification of existing) {
        map.set(notification._id, notification);
    }
    for (const notification of incoming) {
        map.set(notification._id, notification);
    }

    return Array.from(map.values()).sort((a, b) => {
        const aTime = parseNotificationDate(a.createdAt)?.getTime() ?? 0;
        const bTime = parseNotificationDate(b.createdAt)?.getTime() ?? 0;
        return bTime - aTime;
    });
};

const matchesBulkReadFilter = (
    notification: Notification,
    organization?: string,
    category?: string
): boolean => {
    if (notification.read) return false;
    if (category && notification.category !== category) return false;

    if (!organization) return true;

    if (category === "reminder" || category === "audit") {
        return isNotificationInOrgStrict(notification.organization, organization);
    }

    return isNotificationInOrgScope(notification.organization, organization);
};

const incrementSummaryForNotification = (
    summary: NotificationSummary,
    notification: Notification,
    organization?: string
) => {
    if (notification.read) return;

    if (
        !organization ||
        isNotificationInOrgScope(notification.organization, organization)
    ) {
        summary.totalUnread += 1;
    }

    if (
        organization &&
        notification.category === "reminder" &&
        isNotificationInOrgStrict(notification.organization, organization)
    ) {
        summary.taskUnread += 1;
    }

    if (
        organization &&
        notification.category === "audit" &&
        isNotificationInOrgStrict(notification.organization, organization)
    ) {
        summary.auditUnread += 1;
    }
};

const decrementSummaryForNotification = (
    summary: NotificationSummary,
    notification: Notification,
    organization?: string
) => {
    if (notification.read) return;

    if (
        !organization ||
        isNotificationInOrgScope(notification.organization, organization)
    ) {
        summary.totalUnread = Math.max(0, summary.totalUnread - 1);
    }

    if (
        organization &&
        notification.category === "reminder" &&
        isNotificationInOrgStrict(notification.organization, organization)
    ) {
        summary.taskUnread = Math.max(0, summary.taskUnread - 1);
    }

    if (
        organization &&
        notification.category === "audit" &&
        isNotificationInOrgStrict(notification.organization, organization)
    ) {
        summary.auditUnread = Math.max(0, summary.auditUnread - 1);
    }
};

// Async thunk to fetch notifications for a user
export const fetchUserNotifications = createAsyncThunk(
    "notifications/fetchUserNotifications",
    async (
        { userId, from, to }: FetchUserNotificationsArgs,
        { getState, rejectWithValue }
    ) => {
        try {
            const { access_token } = (getState() as RootState).auth;
            const header = {
                Authorization: `Bearer ${access_token}`,
            };
            const { data } = await notificationsApi.getNotificationsByUser(
                userId,
                header,
                { from, to }
            );
            return data as { items: Notification[]; hasMore: boolean };
        } catch (error) {
            return rejectWithValue("Failed to fetch user notifications");
        }
    }
);

export const fetchNotificationSummary = createAsyncThunk(
    "notifications/fetchNotificationSummary",
    async (
        { userId, organization }: FetchNotificationSummaryArgs,
        { getState, rejectWithValue }
    ) => {
        try {
            const { access_token } = (getState() as RootState).auth;
            const header = {
                Authorization: `Bearer ${access_token}`,
            };
            const { data } = await notificationsApi.getNotificationSummary(
                userId,
                header,
                organization
            );
            return data as NotificationSummary;
        } catch (error) {
            return rejectWithValue("Failed to fetch notification summary");
        }
    }
);

export const markNotificationsRead = createAsyncThunk(
    "notifications/markNotificationsRead",
    async (
        { userId, organization, category }: MarkNotificationsReadArgs,
        { getState, rejectWithValue, dispatch }
    ) => {
        try {
            const { access_token } = (getState() as RootState).auth;
            const header = {
                Authorization: `Bearer ${access_token}`,
            };
            const { data } = await notificationsApi.markNotificationsRead(
                userId,
                header,
                { organization, category }
            );
            await dispatch(
                fetchNotificationSummary({ userId, organization })
            );
            return data as { modifiedCount: number };
        } catch (error) {
            return rejectWithValue("Failed to mark notifications as read");
        }
    }
);

// Async thunk to create a notification
export const createNotification = createAsyncThunk(
    "notifications/createNotification",
    async (notificationData: Notification, { getState, rejectWithValue }) => {
        try {
            const state = getState() as RootState;
            const { access_token } = state.auth;
            const header = {
                Authorization: `Bearer ${access_token}`,
            };

            // Check if a similar notification already exists with exactly the same fields
            const existingNotification = state.notifications.notifications.find(
                (n) =>
                    n.title === notificationData.title &&
                    n.message === notificationData.message &&
                    n.severity === notificationData.severity &&
                    n.category === notificationData.category &&
                    n.read === notificationData.read &&
                    n.user === notificationData.user &&
                    n.link === notificationData.link &&
                    n.priority === notificationData.priority &&
                    JSON.stringify(n.data) ===
                        JSON.stringify(notificationData.data) &&
                    n.expiresAt?.toISOString() ===
                        notificationData.expiresAt?.toISOString()
            );

            if (existingNotification) {
                console.log(
                    "Exact match for notification already exists:",
                    existingNotification
                );
                return existingNotification;
            }

            const { data } = await notificationsApi.createNotification(
                notificationData as any,
                header
            );
            return data;
        } catch (error) {
            return rejectWithValue("Failed to create notification");
        }
    }
);

// Async thunk to update a notification
export const updateNotification = createAsyncThunk(
    "notifications/updateNotification",
    async (
        { _id, body }: { _id: string; body: any },
        { getState, rejectWithValue }
    ) => {
        try {
            const { access_token } = (getState() as RootState).auth;
            const header = {
                Authorization: `Bearer ${access_token}`,
            };
            const { data } = await notificationsApi.updateNotification(
                _id,
                body,
                header
            );
            return data;
        } catch (error) {
            return rejectWithValue("Failed to update notification");
        }
    }
);

// Async thunk to delete a notification
export const deleteNotification = createAsyncThunk(
    "notifications/deleteNotification",
    async (_id: string, { getState, rejectWithValue }) => {
        try {
            const { access_token } = (getState() as RootState).auth;
            const header = {
                Authorization: `Bearer ${access_token}`,
            };
            const { data } = await notificationsApi.deleteNotification(
                _id,
                header
            );
            return data;
        } catch (error) {
            return rejectWithValue("Failed to delete notification");
        }
    }
);

const notificationsSlice = createSlice({
    name: "notifications",
    initialState,
    reducers: {
        clearNotifications: (state) => {
            state.notifications = [];
            state.summary = emptySummary;
            state.hasMore = false;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchUserNotifications.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchUserNotifications.fulfilled, (state, action) => {
                state.loading = false;
                const { items, hasMore } = action.payload;
                const append = action.meta.arg.append ?? false;

                state.notifications = append
                    ? mergeNotifications(state.notifications, items)
                    : items;
                state.hasMore = hasMore;
            })
            .addCase(fetchUserNotifications.rejected, (state, action) => {
                state.loading = false;
                state.error =
                    (action.payload as string) ||
                    action.error.message ||
                    "Failed to fetch notifications";
            })
            .addCase(fetchNotificationSummary.pending, (state) => {
                state.summaryLoading = true;
            })
            .addCase(fetchNotificationSummary.fulfilled, (state, action) => {
                state.summaryLoading = false;
                state.summary = action.payload;
                state.summaryOrganizationId = action.meta.arg.organization;
            })
            .addCase(fetchNotificationSummary.rejected, (state) => {
                state.summaryLoading = false;
            })
            .addCase(markNotificationsRead.fulfilled, (state, action) => {
                const { organization, category } = action.meta.arg;
                state.notifications = state.notifications.map((notification) =>
                    matchesBulkReadFilter(notification, organization, category)
                        ? { ...notification, read: true }
                        : notification
                );
            })
            .addCase(createNotification.fulfilled, (state, action) => {
                const existingIndex = state.notifications.findIndex(
                    (notification) => notification._id === action.payload._id
                );
                if (existingIndex === -1) {
                    state.notifications.unshift(action.payload);
                }

                incrementSummaryForNotification(
                    state.summary,
                    action.payload,
                    state.summaryOrganizationId
                );
            })
            .addCase(updateNotification.fulfilled, (state, action) => {
                const index = state.notifications.findIndex(
                    (notification) => notification._id === action.payload._id
                );
                const previous =
                    index !== -1 ? state.notifications[index] : undefined;

                if (index !== -1) {
                    state.notifications[index] = action.payload;
                }

                if (previous && !previous.read && action.payload.read) {
                    decrementSummaryForNotification(
                        state.summary,
                        previous,
                        state.summaryOrganizationId
                    );
                }
            })
            .addCase(deleteNotification.fulfilled, (state, action) => {
                const deleted = state.notifications.find(
                    (notification) => notification._id === action.payload._id
                );

                state.notifications = state.notifications.filter(
                    (notification) => notification._id !== action.payload._id
                );

                if (deleted) {
                    decrementSummaryForNotification(
                        state.summary,
                        deleted,
                        state.summaryOrganizationId
                    );
                }
            });
    },
});

export const { clearNotifications } = notificationsSlice.actions;

export const notificationReducer = notificationsSlice.reducer;
